import type { FieldValues, Resolver } from 'react-hook-form';
import type { ZodType } from 'zod';

/**
 * Bridges a Zod schema to react-hook-form's `resolver` prop.
 *
 * Hand-written instead of pulling in `@hookform/resolvers`: that package
 * declares optional peers on a schema-library aggregator whose version range
 * conflicts with this tree, and it is installed here to do exactly what the
 * fifteen lines below do. A resolver is just a function of
 * `(values) => { values, errors }`.
 */
export const zodResolver =
  <T extends FieldValues>(schema: ZodType<T>): Resolver<T> =>
  async (values) => {
    const result = schema.safeParse(values);

    if (result.success) return { values: result.data, errors: {} };

    // First message per path wins: the field shows one error at a time, and
    // Zod reports issues in declaration order.
    const errors: Record<string, { type: string; message: string }> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      if (path && !errors[path]) errors[path] = { type: issue.code, message: issue.message };
    }

    return { values: {}, errors } as ReturnType<Resolver<T>>;
  };

export default zodResolver;
